/**
 * 프롬프트 관리 유틸리티
 * AI 변형용 프롬프트 생성 및 관리
 */

export type ElementType = 'background' | 'character' | 'title' | 'subtitle' | 'author' | 'publisher'

interface PromptSource {
  custom?: string       // UI에서 수정 중
  final?: string        // DB 저장된 최종본
  generated?: string    // AI가 생성한 초기본
  analysis?: string     // 분석 원본
}

/**
 * 타입별 기본 프롬프트 템플릿 (한글)
 */
export const DEFAULT_TRANSFORM_PROMPTS_KR = {
  background: (analysisText: string) =>
    `${analysisText} 배경을 생성합니다.

다음 요소에만 집중하세요:
- 풍경, 환경, 설정

다음 요소는 제외하세요:
- 인물, 캐릭터
- 텍스트, 타이포그래피
- 시각 효과`,

  character: (analysisText: string) =>
    `${analysisText} 인물을 생성합니다.

다음 요소에만 집중하세요:
- 인물/캐릭터의 외모, 의상, 포즈, 표정

다음 요소는 제외하세요:
- 배경, 풍경, 환경
- 텍스트, 타이포그래피
- 시각 효과`,

  title: (analysisText: string) =>
    `${analysisText} 제목 타이포그래피를 생성합니다.

다음 요소에만 집중하세요:
- 제목 텍스트, 글자, 레터링 디자인

다음 요소는 제외하세요:
- 배경, 풍경
- 인물, 캐릭터
- 시각 효과`,

  subtitle: (analysisText: string) =>
    `${analysisText} 부제목 타이포그래피를 생성합니다.

다음 요소에만 집중하세요:
- 부제목 텍스트, 글자, 레터링 디자인

다음 요소는 제외하세요:
- 배경, 풍경
- 인물, 캐릭터
- 시각 효과`,

  author: (analysisText: string) =>
    `${analysisText} 저자명 타이포그래피를 생성합니다.

다음 요소에만 집중하세요:
- 저자명 텍스트, 글자 디자인

다음 요소는 제외하세요:
- 배경, 풍경
- 인물, 캐릭터
- 시각 효과`,

  publisher: (analysisText: string) =>
    `${analysisText} 출판사명 타이포그래피를 생성합니다.

다음 요소에만 집중하세요:
- 출판사명 텍스트, 글자 디자인

다음 요소는 제외하세요:
- 배경, 풍경
- 인물, 캐릭터
- 시각 효과`
}

/**
 * 분석 결과를 안전하게 파싱
 * JSON이면 필요한 필드 추출, 아니면 텍스트 그대로
 */
export const parseAnalysis = (analysisText: string | null | undefined): string => {
  if (!analysisText?.trim()) return ''

  try {
    const parsed = JSON.parse(analysisText)

    // JSON 구조에서 프롬프트 추출
    if (parsed.prompt) return String(parsed.prompt).trim()
    if (parsed.description) return String(parsed.description).trim()

    // 여러 필드를 조합
    const parts: string[] = []
    if (parsed.setting) parts.push(String(parsed.setting))
    if (parsed.mood) parts.push(String(parsed.mood))
    if (parsed.style) parts.push(String(parsed.style))

    if (parts.length > 0) return parts.join(', ')

    // JSON이지만 알 수 없는 구조면 문자열로 변환
    return String(analysisText).trim()
  } catch {
    // JSON이 아니면 텍스트 그대로
    return String(analysisText).trim()
  }
}

/**
 * 분석 텍스트에서 중복 문구 제거
 */
const cleanAnalysisText = (analysisText: string): string => {
  return analysisText
    // "을/를 생성합니다" 패턴 제거
    .replace(/을\s*생성합니다/g, '')
    .replace(/를\s*생성합니다/g, '')
    .replace(/\(를\)\s*생성합니다/g, '')
    .replace(/생성합니다/g, '')
    // "을/를 만듭니다" 패턴 제거
    .replace(/을\s*만듭니다/g, '')
    .replace(/를\s*만듭니다/g, '')
    .replace(/\(를\)\s*만듭니다/g, '')
    .replace(/만듭니다/g, '')
    // 영문 "Create " 제거
    .replace(/^create\s+/i, '')
    // 연속 공백 제거
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * 분석 결과로부터 변형용 프롬프트 생성
 * 중복 문구 방지
 */
export const buildPromptFromTemplate = (
  elementType: ElementType,
  analysisText: string
): string => {
  const cleanAnalysis = cleanAnalysisText(analysisText)

  if (!cleanAnalysis) {
    return DEFAULT_TRANSFORM_PROMPTS_KR[elementType]('기본')
  }

  let prompt = DEFAULT_TRANSFORM_PROMPTS_KR[elementType](cleanAnalysis)

  // 투명 배경 지시 추가 (배경 제외)
  if (elementType !== 'background') {
    prompt = addTransparencyDirective(prompt)
  }

  return prompt
}

/**
 * 배경 분리 지시를 안전하게 추가
 * 이미 있으면 추가하지 않음
 */
export const addTransparencyDirective = (prompt: string): string => {
  const backgroundKeywords = [
    '투명',
    'transparent',
    'alpha channel',
    '배경 제거',
    'no background',
    'remove background',
    '단색',
    '분리',
    'solid color',
    'solid background'
  ]

  // 이미 배경 분리 지시가 있는지 확인
  const hasTransparency = backgroundKeywords.some(keyword =>
    prompt.toLowerCase().includes(keyword.toLowerCase())
  )

  if (hasTransparency) {
    return prompt // 이미 있으면 그대로
  }

  // 없으면 추가
  return `${prompt}

중요: 배경은 단색으로 피사체와 분리되어 제거하기 용이하게 할것`
}

/**
 * 프롬프트 우선순위에 따라 최종 프롬프트 결정
 * 1. 사용자가 UI에서 직접 수정한 값 (custom)
 * 2. DB의 최종 수정본 (final)
 * 3. DB의 초기 생성 프롬프트 (generated)
 * 4. 시스템 기본 템플릿 (analysis 기반)
 */
export const getEffectivePrompt = (
  elementType: ElementType,
  sources: PromptSource
): string => {
  // 1순위: 사용자 수정본
  if (sources.custom?.trim()) {
    return sources.custom.trim()
  }

  // 2순위: DB 최종본
  if (sources.final?.trim()) {
    return sources.final.trim()
  }

  // 3순위: AI 생성본
  if (sources.generated?.trim()) {
    return sources.generated.trim()
  }

  // 4순위: 분석 결과로 템플릿 생성
  const analysisText = parseAnalysis(sources.analysis)
  return buildPromptFromTemplate(elementType, analysisText)
}

/**
 * 중복된 문구를 제거하는 헬퍼 함수
 */
const removeDuplicatePhrases = (text: string, phrase: string): string => {
  // 정확한 문구 매칭을 위한 이스케이프
  const escapedPhrase = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  // 모든 문구 발생 위치를 찾아서 첫 번째만 남기고 제거
  const regex = new RegExp(escapedPhrase, 'g')
  const matches = text.match(regex)

  if (!matches || matches.length <= 1) {
    return text // 중복 없음
  }

  // 첫 번째 발생을 찾아서 표시
  let result = text
  let firstMatch = true

  result = result.replace(regex, (match) => {
    if (firstMatch) {
      firstMatch = false
      return match // 첫 번째는 유지
    }
    return '' // 나머지는 제거
  })

  // 연속 공백이나 점 정리
  result = result.replace(/\.\s*\./g, '.')
  result = result.replace(/\s+/g, ' ')

  return result
}

/**
 * 공백을 정규화하는 헬퍼 함수
 */
const normalizeWhitespace = (text: string): string => {
  return text
    // 연속 공백을 하나로
    .replace(/[ \t]+/g, ' ')
    // 3개 이상 연속 줄바꿈을 2개로
    .replace(/\n\s*\n\s*\n+/g, '\n\n')
}

/**
 * 프롬프트 위생 검사 (중복 제거, 공백 정리)
 * 변형 요청 전 마지막 체크
 */
export const sanitizePrompt = (prompt: string): string => {
  let result = prompt

  // 1. 중복 문구 제거
  result = removeDuplicatePhrases(result, '생성합니다')
  result = removeDuplicatePhrases(result, '제외합니다')
  result = removeDuplicatePhrases(result, '집중하세요')

  // 2. 투명 배경 지시 중복 제거
  const transparencyPattern = /중요:\s*배경을\s*완전히\s*투명하게.*?\n/gi
  const transparencyMatches = result.match(transparencyPattern)
  if (transparencyMatches && transparencyMatches.length > 1) {
    // 첫 번째만 남기고 나머지 제거
    result = result.replace(transparencyPattern, '')
    result = result + '\n\n' + transparencyMatches[0]
  }

  // 3. 공백 정규화
  result = normalizeWhitespace(result)

  // 4. 각 줄의 앞뒤 공백 제거 (줄바꿈은 유지)
  result = result
    .split('\n')
    .map(line => line.trim())
    .join('\n')

  // 5. 전체 trim
  return result.trim()
}
